import { Loading } from '@affine/component';
import { resolvePermissionPeoplePresentation } from '@affine/core/modules/permissions/utils/permission-people-presentation';
import {
  PermissionGroupRow,
  PermissionMemberRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { FolderRole as FolderRoleEnum } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useMemo } from 'react';

import type { UseFolderShareResult } from '../hooks/use-folder-share';
import {
  FolderPermissionExpandableSection,
  FolderPermissionRoleSelector,
  useObjectRoleLabel,
} from './folder-permission-core';
import * as styles from './folder-share-modal.css';

export interface FolderAccessOverviewProps {
  access: Pick<
    UseFolderShareResult,
    'error' | 'folderGroupRoles' | 'grantedUsers' | 'isLoading' | 'owner'
  >;
}

/**
 * Read-only projection of the canonical folder permission model.
 *
 * This component deliberately owns no permission rules, data loading, or
 * mutations. It renders the canonical folder-access result with the same rows,
 * role labels, and inheritance labels as the regular folder permission dialog.
 */
export const FolderAccessOverview = ({ access }: FolderAccessOverviewProps) => {
  const t = useI18n();
  const getRoleLabel = useObjectRoleLabel(t);
  const { owner, grantedUsers, folderGroupRoles, isLoading, error } = access;
  const peoplePresentation = useMemo(
    () =>
      resolvePermissionPeoplePresentation({
        owner,
        users: grantedUsers,
        getOwnerId: currentOwner => currentOwner.id,
        getUserId: user => user.user.id,
        isOwnerUser: user => user.role === FolderRoleEnum.Owner,
        isUserVisible: user => !user.isAreaBlocked,
      }),
    [grantedUsers, owner]
  );

  if (isLoading) {
    return (
      <div className={styles.loading} data-testid="folder-access-overview">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error} data-testid="folder-access-overview">
        {error}
      </div>
    );
  }

  const visibleGroups = folderGroupRoles.filter(group => !group.isAreaBlocked);

  return (
    <div className={styles.container} data-testid="folder-access-overview">
      <FolderPermissionExpandableSection
        title={t['com.streamlain.folder-share.people']()}
      >
        <div className={styles.accessList}>
          {peoplePresentation.owner ? (
            <PermissionMemberRow
              user={peoplePresentation.owner}
              roleSelector={
                <span className={styles.roleLabel}>
                  {getRoleLabel('Owner')}
                </span>
              }
            />
          ) : null}
          {peoplePresentation.showOwnerDivider ? (
            <div className={styles.accessDivider} role="separator" />
          ) : null}
          {peoplePresentation.visibleNonOwners.map(user => (
            <PermissionMemberRow
              key={user.user.id}
              dataTestId={`folder-access-overview-user-${user.user.id}`}
              user={user.user}
              roleSelector={
                <FolderPermissionRoleSelector
                  currentRole={user.role}
                  canManage={false}
                  onChangeRole={() => undefined}
                  getRoleLabel={getRoleLabel}
                  sourceType={user.sourceType}
                  sourceName={user.sourceName}
                  isDirectOverride={user.isDirectOverride}
                />
              }
            />
          ))}
          {!owner && peoplePresentation.visibleNonOwners.length === 0 ? (
            <div className={styles.emptyState}>
              {t['com.streamlain.folder-share.no-users']()}
            </div>
          ) : null}
        </div>
      </FolderPermissionExpandableSection>
      <FolderPermissionExpandableSection
        title={t['com.streamlain.folder-share.groups']()}
      >
        <div className={styles.accessList}>
          {visibleGroups.map(group => (
            <PermissionGroupRow
              key={group.groupId}
              dataTestId={`folder-access-overview-group-${group.groupId}`}
              groupName={group.groupName}
              roleSlot={
                <FolderPermissionRoleSelector
                  currentRole={group.role}
                  canManage={false}
                  onChangeRole={() => undefined}
                  getRoleLabel={getRoleLabel}
                  sourceType={group.sourceType}
                  sourceName={group.sourceName}
                  isDirectOverride={group.isDirectOverride}
                />
              }
            />
          ))}
          {visibleGroups.length === 0 ? (
            <div className={styles.emptyState}>
              {t['com.streamlain.folder-share.no-groups']?.() ??
                'No groups have access yet.'}
            </div>
          ) : null}
        </div>
      </FolderPermissionExpandableSection>
    </div>
  );
};
